import React, { useReducer, useEffect, useContext, useState } from 'react';
import ThemeContext from './themeContext';
import AuthContext from '../auth/authContext';
import themeReducer from './themeReducer';
import {
  TOGGLE_THEME,
  SET_THEME
} from '../types';

const ThemeState = props => {
  const authContext = useContext(AuthContext);
  const { user, isAuthenticated, setDarkMode } = authContext || {};

  // Check if dark mode preference is saved in localStorage or user profile
  const loadSavedTheme = () => {
    // Use localStorage as the primary source of truth to avoid flashing
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) {
      return JSON.parse(savedTheme);
    }
    
    // If no localStorage preference, and user is authenticated, use their preference
    if (isAuthenticated && user && user.darkModeEnabled !== undefined) {
      return user.darkModeEnabled;
    }
    
    // Default to light mode if no preference is found
    return false;
  };

  const initialState = {
    isDarkMode: loadSavedTheme()
  };

  const [state, dispatch] = useReducer(themeReducer, initialState);

  // Create toggle state first to ensure it's available for all hooks
  const [isTogglingTheme, setIsTogglingTheme] = useState(false);
  const [userInitiatedToggle, setUserInitiatedToggle] = useState(false);

  // Sync theme with user's profile when authentication state changes, but only during initial load
  useEffect(() => {
    // This should only run when the user first logs in, not during toggling
    if (!userInitiatedToggle && isAuthenticated && user && user.darkModeEnabled !== undefined) {
      // Only sync if:
      // 1. It's not a user initiated toggle
      // 2. The user profile theme is different from current state
      // 3. We haven't already loaded a theme preference from localStorage
      const hasLocalTheme = localStorage.getItem('darkMode') !== null;
      
      if (user.darkModeEnabled !== state.isDarkMode && !hasLocalTheme) {
        console.log('Initial sync from user profile:', user.darkModeEnabled);
        dispatch({
          type: SET_THEME,
          payload: user.darkModeEnabled
        });
      }
    }
  }, [isAuthenticated, user, userInitiatedToggle]);

  // Initialize theme attribute on app load
  useEffect(() => {
    // Apply theme immediately when component mounts
    document.documentElement.setAttribute(
      'data-theme',
      state.isDarkMode ? 'dark' : 'light'
    );
    
    // Mark as initial load
    localStorage.setItem('initialThemeLoad', 'complete');
  }, [state.isDarkMode]);
  
  // Set the data-theme attribute on the root element whenever theme changes
  useEffect(() => {
    // Only update localStorage if it's not during a user toggle
    // (The toggle function handles localStorage updates during toggling)
    if (!isTogglingTheme) {
      localStorage.setItem('darkMode', JSON.stringify(state.isDarkMode));
    }
    
    // Always update data-theme attribute on HTML element for immediate UI update
    document.documentElement.setAttribute(
      'data-theme',
      state.isDarkMode ? 'dark' : 'light'
    );
  }, [state.isDarkMode, isTogglingTheme]);
  
  // We've removed the separate effect for profile updates
  // The profile updates are now handled directly in the toggleTheme function
  // This prevents race conditions and circular updates

  // Toggle theme function with protection against auto-reverting
  const toggleTheme = () => {
    // Mark this as a user initiated toggle to block auto-syncing
    setUserInitiatedToggle(true);
    setIsTogglingTheme(true);
    
    // Get the new theme value (opposite of current)
    const newDarkMode = !state.isDarkMode;
    
    // Update localStorage immediately to prevent conflicts
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
    
    // Dispatch theme toggle action
    dispatch({
      type: TOGGLE_THEME
    });
    
    // Apply the change to user profile with a delay to ensure it happens after the UI update
    if (isAuthenticated && setDarkMode && user) {
      setTimeout(() => {
        if (user.darkModeEnabled !== newDarkMode) {
          console.log('Saving toggled theme to profile:', newDarkMode);
          setDarkMode(newDarkMode);
        }
        
        // Reset toggle flags after a longer delay
        setTimeout(() => {
          setUserInitiatedToggle(false);
          setIsTogglingTheme(false);
        }, 1000);
      }, 200);
    } else {
      // Reset toggle flags if not authenticated
      setTimeout(() => {
        setUserInitiatedToggle(false);
        setIsTogglingTheme(false);
      }, 500);
    }
  };

  // Set specific theme
  const setTheme = isDarkMode => {
    // Clear the initial load flag to indicate this is an explicit user action
    localStorage.removeItem('initialThemeLoad');
    
    dispatch({
      type: SET_THEME,
      payload: isDarkMode
    });
  };

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode: state.isDarkMode,
        toggleTheme,
        setTheme
      }}
    >
      {props.children}
    </ThemeContext.Provider>
  );
};

export default ThemeState;